import { Button, Dropdown, Menu, Drawer } from "antd";
import { useCallback, useMemo, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Input, SwitchButton } from "components/base";
import { Link, useHistory, useLocation } from "react-router-dom";
import {
  getVehicle,
  setModalName,
  setSelectedVehicle,
  setSelectedVehicleModel,
  setVehiclesSearch,
} from "store/actions";
import {
  actionMenu,
  exportIcon,
  plusIcon,
  searchIcon,
  zonesSmallIcon,
  addAssigneeIcon,
  archiveIcon,
  serviceIcon,
  uploadIcon,
  uploadIconSmall,
  settingsIcon,
  settingsIcon2,
  settingsIcon3,
  editIcon,
} from "svgs";
import { ROUTES } from "utils/constants";
import { AppState } from "store";
import {
  MenuOutlined,
  SearchOutlined,
  PlusOutlined,
  UploadOutlined,
  TableOutlined,
  EnvironmentOutlined,
  SettingOutlined,
  CloseOutlined,
  MoreOutlined,
} from "@ant-design/icons";
import "styles/components/tab-headers/vehicles-header.scss";

export default function VehiclesHeader() {
  const location = useLocation();
  const { pathname } = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isTablet, setIsTablet] = useState(
    window.innerWidth >= 768 && window.innerWidth < 992
  );

  const {
    common: { userType = 1 },
  } = useSelector<AppState, AppState>((state) => state);

  const selectedView = location.pathname.split("/")[2];
  const isVehicleModel = pathname === ROUTES.vehicle_models_list;
  const { showbulkActionsVehicles } = useSelector<AppState, AppState["vehicles"]>(
    (state) => state.vehicles
  );
  let defaultTabByUser = userType == 5 ? "map" : "table";
  // Handle responsive breakpoints
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      setIsTablet(window.innerWidth >= 768 && window.innerWidth < 992);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    dispatch(setVehiclesSearch(""));
  }, [dispatch]);

  const searchValue = useSelector<AppState, string>(
    (state) => state.vehicles.search || ""
  );

  const onAddVehicles = useCallback(() => {
    dispatch(setModalName("add_firmware_vehicle"));
    setMobileMenuOpen(false);
  }, [dispatch]);

  const onBulkVehiclesFirmwareUpdate = useCallback(() => {
    dispatch(setModalName("bulk_update_vehicle_firmware"));
    setMobileMenuOpen(false);
  }, [dispatch]);

  const onMqttServerChange = useCallback(() => {
    dispatch(setModalName("change_mqtt_server"));
    setMobileMenuOpen(false);
  }, [dispatch]);

  const onUploadFirmware = useCallback(() => {
    dispatch(setModalName("add_model_firmware"));
    setMobileMenuOpen(false);
  }, [dispatch]);

  const onAddVehicle = useCallback(() => {
    dispatch(setModalName("add_vehicle"));
    setMobileMenuOpen(false);
  }, [dispatch]);

  const onAddVehicleModel = useCallback(() => {
    dispatch(setModalName("add_vehicle_model"));
    setMobileMenuOpen(false);
  }, [dispatch]);

  const onSwitchView = useCallback(
    (event) => {
      const { value } = event.target;
      dispatch(setVehiclesSearch(""));
      history.push(
        value == "Vehicles" ? ROUTES.vehicles_list : ROUTES.vehicle_models_list
      );
      setMobileMenuOpen(false);
    },
    [history]
  );

  const onMapSwitchView = useCallback(
    (event) => {
      const { value } = event.target;
      history.push(`${ROUTES.vehicles}/${value}`);
      setMobileMenuOpen(false);
    },
    [history]
  );

  const onImportCsv = useCallback(() => {
    dispatch(setModalName("import_csv"));
    setMobileMenuOpen(false);
  }, [dispatch]);

  const onChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      dispatch(setVehiclesSearch(event.target.value));
    },
    [dispatch]
  );

  const menu = (
    <Menu className="vehicle-drop-menu">
      <Menu.Item key="0" onClick={onImportCsv}>
        <span>Import CSV</span>
      </Menu.Item>
    </Menu>
  );

  // Mobile menu content
  const mobileMenuContent = (
    <div className="mobile-menu-content">
      {/* View Switchers */}
      {(userType == 1 || userType == 2 || userType == 8) && (
        <div className="mobile-menu-section">
          <h4 className="section-title">Content Type</h4>
          <SwitchButton
            label=""
            options={["Vehicles", "Vehicle Models"]}
            defaultOption={isVehicleModel ? "Vehicle Models" : "Vehicles"}
            onChange={onSwitchView}
            type="secondary"
          />
        </div>
      )}

      {!isVehicleModel && (
        <div className="mobile-menu-section">
          <h4 className="section-title">View Mode</h4>
          <SwitchButton
            label=""
            options={["table", "map"]}
            defaultOption={selectedView ?? defaultTabByUser}
            onChange={onMapSwitchView}
            type="secondary"
          />
        </div>
      )}

      {/* Actions */}
      {(userType == 1 || userType == 2 || userType == 8) && (
        <div className="mobile-menu-section">
          <h4 className="section-title">Actions</h4>
          <div className="mobile-action-buttons">
            {isVehicleModel ? (
              <>
                <Button
                  type="default"
                  icon={<UploadOutlined />}
                  onClick={onUploadFirmware}
                  block
                >
                  Upload Firmware
                </Button>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={onAddVehicleModel}
                  block
                >
                  Add Vehicle Model
                </Button>
              </>
            ) : (
              <>
                <Button type="default" onClick={onImportCsv} block>
                  Import CSV
                </Button>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={onAddVehicle}
                  block
                >
                  Add Vehicle
                </Button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Bulk Actions */}
      {showbulkActionsVehicles && !isVehicleModel && (
        <div className="mobile-menu-section">
          <h4 className="section-title">Bulk Actions</h4>
          <div className="mobile-action-buttons">
            <Button
              icon={<SettingOutlined />}
              onClick={onMqttServerChange}
              block
            >
              Change Server
            </Button>
            <Button
              icon={<UploadOutlined />}
              onClick={onBulkVehiclesFirmwareUpdate}
              block
            >
              Update Firmware
            </Button>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="vehicles-header common-header">
      {/* Desktop & Tablet View */}
      <div className="header-desktop">
        {/* Search Input */}
        {!isVehicleModel && (
          <div className="search-wrapper">
            <Input
              customClasses="--standard"
              placeholder="Search Vehicles..."
              prefix={searchIcon}
              value={searchValue}
              onChange={onChange}
            />
          </div>
        )}

        {/* Bulk Actions */}
        {showbulkActionsVehicles && !isVehicleModel && (
          <article className="bulk-actions">
            <Button icon={editIcon} onClick={onMqttServerChange}>
              <span className="btn-text">Server</span>
            </Button>
            <Button icon={uploadIconSmall} onClick={onBulkVehiclesFirmwareUpdate}>
              <span className="btn-text">Firmware</span>
            </Button>
          </article>
        )}

        {/* Actions Container */}
        <div className="actions-container">
          {(userType == 1 || userType == 2 || userType == 8) && (
            <>
              <SwitchButton
                label=""
                options={["Vehicles", "Vehicle Models"]}
                defaultOption={isVehicleModel ? "Vehicle Models" : "Vehicles"}
                onChange={onSwitchView}
                type="secondary"
              />
              <div className="vertical-separator" />
            </>
          )}

          {(!isVehicleModel && (userType == 1 || userType == 5 || userType == 2 || userType == 8)) && (
            <SwitchButton
              label={isTablet ? "" : "Select View:"}
              options={["table", "map"]}
              defaultOption={selectedView ?? defaultTabByUser}
              onChange={onMapSwitchView}
              type="secondary"
            />
          )}

          {(userType == 1 || userType == 2 || userType == 8) && (
            <>
              {isVehicleModel ? (
                <>
                  <Button type="primary" onClick={onUploadFirmware}>
                    <span className="btn-icon">{uploadIconSmall}</span>
                    <span className="btn-text">Upload Firmware</span>
                  </Button>
                  <div className="vertical-separator" />
                  <Button
                    type="primary"
                    icon={<span className="custom-icon">{plusIcon}</span>}
                    onClick={onAddVehicleModel}
                  >
                    <span className="btn-text">Add Vehicle Model</span>
                  </Button>
                </>
              ) : (
                <>
                  <div className="vertical-separator" />
                  <Dropdown
                    overlay={menu}
                    trigger={["click"]}
                    className="vehicle-menu"
                  >
                    <a
                      className="ant-dropdown-link"
                      onClick={(e) => e.preventDefault()}
                    >
                      {actionMenu}
                    </a>
                  </Dropdown>
                  <Button
                    type="primary"
                    icon={<span className="custom-icon">{plusIcon}</span>}
                    onClick={onAddVehicle}
                  >
                    <span className="btn-text">Add Vehicle</span>
                  </Button>
                </>
              )}
            </>
          )}
        </div>
      </div>

      {/* Mobile View */}
      <div className="header-mobile">
        {/* Top Row: Search & Menu */}
        <div className="mobile-top-row">
          {/* Search Toggle or Input */}
          {!isVehicleModel && (
            <>
              {mobileSearchOpen ? (
                <div className="mobile-search-expanded">
                  <Input
                    customClasses="--standard mobile-search-input"
                    placeholder="Search Vehicles..."
                    prefix={searchIcon}
                    value={searchValue}
                    onChange={onChange}
                    autoFocus
                  />
                  <Button
                    type="text"
                    icon={<CloseOutlined />}
                    onClick={() => setMobileSearchOpen(false)}
                    className="search-close-btn"
                  />
                </div>
              ) : (
                <Button
                  type="text"
                  icon={<SearchOutlined />}
                  onClick={() => setMobileSearchOpen(true)}
                  className="mobile-search-btn"
                />
              )}
            </>
          )}

          {/* View Toggle - Quick Access */}
          {!isVehicleModel && !mobileSearchOpen && (
            <div className="mobile-view-toggle">
              <Button
                type={selectedView === "table" ? "primary" : "default"}
                icon={<TableOutlined />}
                onClick={() => history.push(`${ROUTES.vehicles}/table`)}
                className="view-toggle-btn"
              />
              {(userType <= 2 || userType == 5 || userType == 6 || userType == 8 ) &&
              <Button
                type={selectedView === "map" ? "primary" : "default"}
                icon={<EnvironmentOutlined />}
                onClick={() => history.push(`${ROUTES.vehicles}/map`)}
                className="view-toggle-btn"
              /> }
            </div>
          )}

          {/* Primary Action Button */}
          {!mobileSearchOpen && (userType == 1 || userType == 2) && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={isVehicleModel ? onAddVehicleModel : onAddVehicle}
              className="mobile-add-btn"
            >
              <span className="mobile-add-text">
                {isVehicleModel ? "Add Model" : "Add"}
              </span>
            </Button>
          )}

          {/* Menu Button */}
          {!mobileSearchOpen && (
            <Button
              type="text"
              icon={actionMenu}
              onClick={() => setMobileMenuOpen(true)}
              className="mobile-menu-btn"
            />
          )}
        </div>

        {/* Bulk Actions Bar - Mobile */}
        {showbulkActionsVehicles && !isVehicleModel && (
          <div className="mobile-bulk-actions">
            <Button
              size="small"
              icon={<SettingOutlined />}
              onClick={onMqttServerChange}
            >
              Server
            </Button>
            <Button
              size="small"
              icon={<UploadOutlined />}
              onClick={onBulkVehiclesFirmwareUpdate}
            >
              Firmware
            </Button>
          </div>
        )}
         <h1 style={{ flex: 1,paddingTop: "6px", alignItems: "left" }}> {isVehicleModel ? "Vehicle Model" : "Vehicles"}</h1>
      </div>

      {/* Mobile Drawer Menu */}
      <Drawer
        title="Menu"
        placement="right"
        onClose={() => setMobileMenuOpen(false)}
        visible={mobileMenuOpen}
        className="vehicles-mobile-drawer"
        width={280}
      >
        {mobileMenuContent}
      </Drawer>
    </div>
  );
}